import { Link } from 'react-router-dom';
import { Alert } from '@clay-run/glaze/alert';
import { useAppBasePath } from '../../_shared/base-path';
import { useLinkedInPost, useLinkedInStatus } from '../api-client';
import { LINKEDIN_GROWTH_DEFAULTS } from '../draft-fixtures';
import { usd } from '../format';
import type { AdRequestDetail, LinkedInRequestFields } from '../types';
import { LinkedInPostPreview } from './AdPreviews';

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-content-tertiary">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

export function LinkedInPublishedAlert({ fields }: { fields: LinkedInRequestFields }) {
  const base = useAppBasePath();
  if (!fields.campaignId) return null;
  return (
    <Alert status={fields.creativeId ? 'success' : 'warning'}>
      {fields.creativeId
        ? 'Created on LinkedIn: a draft campaign with the post as a paused ad. Activate it in Campaign Manager when it should start spending. '
        : "Campaign created on LinkedIn as a draft. The post wasn't attached: this connection can't read the company page's posts (a test account has no page). "}
      <Link to={`${base}/performance/linkedin/${fields.campaignId}`} className="font-medium underline">
        View campaign
      </Link>
    </Alert>
  );
}

export function LinkedInRequestBody({ request, fields }: { request: AdRequestDetail; fields: LinkedInRequestFields }) {
  const post = useLinkedInPost(fields.postUrl);
  const status = useLinkedInStatus();
  return (
    <div className="flex flex-wrap gap-8">
      <div className="w-80 shrink-0">
        <LinkedInPostPreview
          post={post.data?.post ?? null}
          loading={post.isFetching}
          problem={post.data?.problem ?? (post.error ? post.error.message : null)}
        />
      </div>
      <dl className="grid h-fit min-w-0 max-w-xl flex-1 grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
        <Detail label="Team">{request.team || '—'}</Detail>
        <Detail label="Requested by">{request.requesterEmail ?? '—'}</Detail>
        <Detail label="Submitted">{new Date(request.submittedAt).toLocaleString()}</Detail>
        <Detail label="Platform">LinkedIn · boosted post</Detail>
        <Detail label="Post">
          <a href={fields.postUrl} target="_blank" rel="noopener noreferrer" className="break-all underline">
            {fields.postUrl}
          </a>
        </Detail>
        <Detail label="Campaign group">{fields.campaignGroupName}</Detail>
        <Detail label="Copies settings from">{fields.sourceCampaignName}</Detail>
        <Detail label="Daily budget">{usd(request.dailyBudgetUsd)}</Detail>
        <Detail label="Dates">
          {request.startDate} → {request.endDate ?? 'no end date'}
        </Detail>
        <Detail label="Growth defaults">{LINKEDIN_GROWTH_DEFAULTS.join(' · ')}</Detail>
        {request.reviewedAt && (
          <Detail label="Reviewed">
            {new Date(request.reviewedAt).toLocaleString()} by {request.reviewerEmail ?? 'a reviewer'}
          </Detail>
        )}
        {request.canReview && status.data?.account?.test && (
          <Detail label="Publishes to">LinkedIn test ad account (ads never serve)</Detail>
        )}
      </dl>
    </div>
  );
}
